import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { AssetUrls } from 'config/assets';
import {
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  useInitiateTradeMutation,
} from 'store/services/trade/tradeApi';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import { useProfileTradeSidebar } from '../../context/ProfileTradeSidebarContext';
import TradeSidebarCard from './TradeSidebarCard';
import './MobileTradeDrawer.css';

export const MobileTradeDrawer: React.FC = () => {
  const navigate = useNavigate();
  const {
    cards,
    receiverUsername,
    receiverId,
    totalCards,
    totalPoints,
    removeCard,
    updateCard,
    clearAll,
  } = useProfileTradeSidebar();

  // Fetch receiver's profile to get their available points
  const { data: receiverProfile } = useGetPublicProfileQuery(receiverUsername || '', {
    skip: !receiverUsername,
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [initiateTrade] = useInitiateTradeMutation();
  const [isCreatingTrade, setIsCreatingTrade] = useState(false);

  // Don't render if no cards
  if (cards.length === 0) {
    return null;
  }

  const handleCreateTrade = async () => {
    if (!receiverId || cards.length === 0) return;

    setIsCreatingTrade(true);
    let acquiredLockToken: string | null = null;

    try {
      // Step 1: Acquire trade lock
      const lockResult = await createTradeLock({ receiverId }).unwrap();

      if (!lockResult.success || !lockResult.lockToken) {
        toast.error(lockResult.message || 'Failed to acquire trade lock. Please try again.');
        setIsCreatingTrade(false);
        return;
      }

      acquiredLockToken = lockResult.lockToken;

      // Step 2: Initiate the trade
      const tradeResult = await initiateTrade({
        receiverId,
        lockToken: acquiredLockToken,
        cards: cards.map(card => ({
          wantListId: card.wantListId,
          cardId: card.cardId?.toString(),
          quantity: 1,
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          set: card.set,
        })),
      }).unwrap();

      if (tradeResult.success) {
        toast.success('Trade created successfully!');
        clearAll();
        navigate('/sending');
      } else {
        toast.error(tradeResult.message || 'Failed to create trade. Please try again.');
        if (acquiredLockToken) {
          await releaseTradeLock({ receiverId, lockToken: acquiredLockToken });
        }
      }
    } catch (error: any) {
      console.error('Trade creation error:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to create trade. Please try again.';
      toast.error(errorMessage);

      if (acquiredLockToken && receiverId) {
        try {
          await releaseTradeLock({ receiverId, lockToken: acquiredLockToken });
        } catch (releaseError) {
          console.error('Failed to release lock:', releaseError);
        }
      }
    } finally {
      setIsCreatingTrade(false);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove all cards from your trade?')) {
      clearAll();
      setIsExpanded(false);
    }
  };

  const drawerContent = (
    <>
      {/* Overlay when expanded */}
      {isExpanded && (
        <div
          className="mobile-trade-drawer-overlay"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Mini summary bar (collapsed) */}
      {!isExpanded && (
        <div
          className="mobile-trade-drawer-mini"
          onClick={() => setIsExpanded(true)}
        >
          <div className="mobile-trade-drawer-mini-left">
            <img
              src={AssetUrls.icons.navigation.sending}
              alt=""
              className="mobile-trade-drawer-mini-icon"
            />
            <span className="mobile-trade-drawer-mini-count">
              {totalCards} {totalCards === 1 ? 'card' : 'cards'}
            </span>
          </div>
          <div className="mobile-trade-drawer-mini-right">
            <Points points={totalPoints} type="small" />
            <img
              src={AssetUrls.icons.dropdowns.arrowUp}
              alt=""
              className="mobile-trade-drawer-mini-arrow"
            />
          </div>
        </div>
      )}

      {/* Full drawer (expanded) */}
      <div className={`mobile-trade-drawer ${isExpanded ? 'expanded' : ''}`}>
        {/* Header */}
        <div className="mobile-trade-drawer-header">
          <div
            className="mobile-trade-drawer-handle"
            onClick={() => setIsExpanded(false)}
          >
            <div className="mobile-trade-drawer-handle-bar" />
          </div>
          <div className="mobile-trade-drawer-header-content">
            <div className="mobile-trade-drawer-header-left">
              <div className="trade-sidebar-title-row">
                <span className="mobile-trade-drawer-title">
                  Sending to {receiverUsername || 'User'}
                </span>
                <Points type="small" points={receiverProfile?.points ?? 0} />
              </div>
              <span className="mobile-trade-drawer-count">
                {totalCards} {totalCards === 1 ? 'Card' : 'Cards'}
              </span>
            </div>
            <button
              className="mobile-trade-drawer-close"
              onClick={() => setIsExpanded(false)}
            >
              <img
                src={AssetUrls.icons.dropdowns.close}
                alt="Close"
                className="mobile-trade-drawer-close-icon"
              />
            </button>
          </div>
        </div>

        {/* Card list */}
        <div className="mobile-trade-drawer-cards">
          {cards.map(card => (
            <TradeSidebarCard
              key={card.id}
              card={card}
              onUpdate={updateCard}
              onRemove={removeCard}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mobile-trade-drawer-footer">
          <div className="mobile-trade-drawer-total">
            <span className="mobile-trade-drawer-total-label">You will earn:</span>
            <Points points={totalPoints} type="big" />
          </div>

          <div className="mobile-trade-drawer-actions">
            <MagicButton
              label="Clear"
              color="dark-grey"
              onPress={handleClearAll}
              isDisabled={isCreatingTrade}
            />
            <MagicButton
              label={isCreatingTrade ? 'Creating...' : 'Create Trade'}
              color="purple"
              onPress={handleCreateTrade}
              isDisabled={isCreatingTrade}
              isLoading={isCreatingTrade}
            />
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default MobileTradeDrawer;
